import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { faSignInAlt } from "@fortawesome/free-solid-svg-icons/faSignInAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { StyledLoader } from "../ariManagement/updateForm/styles";
import { StyledReactTooltip } from "../shared/actions/tableActions";
import { defaultListMessages } from "../shared/crud/list";
import { IPropertyChainData, useChainData } from "../../components/chain-data-provider";

const messages = {
  insertReservationTitle: {
    id: "menu.reservations.insert",
    defaultMessage: "OK",
  },
  insertReservationCallCenterTitle: {
    id: "menu.reservations.insertLoyal",
    defaultMessage: "OK",
  },
  insertReservation: {
    id: "app.entityList.insertReservationReception",
    defaultMessage: "OK",
  },
  insertReservationInbound: {
    id: "app.entityList.insertReservationInbound",
    defaultMessage: "OK",
  },
  insertReservationOutbound: {
    id: "app.entityList.insertReservationOutbound",
    defaultMessage: "OK",
  },
  login: {
    id: "loyaltyCustomer.login",
    defaultMessage: "login",
  },
};
export type MODE = "inbound" | "outbound" | "reception";

const TableActionButton = styled(Button)`
  height: 33px;
  margin-right: 14px;
`;

export interface InsertReservationCallCenterProps {
  propertyTicker: string;
  email: string;
  isCallCenterUser: boolean;
  engineURL: string;
}
export interface ImpersonateLoyaltyCustomerProps extends InsertReservationCallCenterProps {
  customerId: string;
  sessionBuilder: (customerId: string, openLink: boolean) => Promise<string | undefined>;
}

function generateAffiliate(mode: string, email: string) {
  const withSuffix = email.replace("@", `+${mode}@`);
  return btoa(withSuffix);
}

function generateLink({
  propertyTicker,
  mode,
  email,
  session,
  engineURL,
}: {
  propertyTicker: string;
  mode: MODE;
  email: string;
  session?: string;
  engineURL: string;
}): string {
  const encodedAffiliate = generateAffiliate(mode, email);
  const origin = mode === "reception" ? "reception" : "callcenter";
  const sessionSegment = session ? `&session=${session}` : "";
  return (
    `${engineURL}/${propertyTicker}` +
    `?encodedAffiliate=${encodedAffiliate}` +
    `&prom=${mode}` +
    `&mode=${mode}` +
    `&origin=${origin}` +
    `&utm_medium=${mode === "reception" ? "reception" : "callcenter"}` +
    `&utm_source=${mode}` +
    `&utm_term=${email}` +
    `&disableAbTest=1${sessionSegment}`
  );
}
const validTickers = [
  "DP_AGR",
  "demo_lucic",
  "DP_BOCAYNA",
  "DP_GTAGORO",
  "DP_PORTALS",
  "DP_TACANDE",
  "DP_TAGORO",
  "DP_TIGOTAN",
  "HCRISTINA",
];

export function isDreamplaceEngine(propertyTicker: string) {
  return validTickers.includes(propertyTicker);
}

export function InsertReservationCallCenter(
  props: InsertReservationCallCenterProps
): JSX.Element | null {
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const isChain = Boolean(chainData?.isChain);
  const linkMap: Record<MODE, string> = useMemo(() => {
    return {
      inbound: generateLink({
        propertyTicker: props.propertyTicker,
        mode: "inbound",
        email: props.email,
        engineURL: props.engineURL + (isChain ? "/chain" : "/hotel"),
      }),
      outbound: generateLink({
        propertyTicker: props.propertyTicker,
        mode: "outbound",
        email: props.email,
        engineURL: props.engineURL + (isChain ? "/chain" : "/hotel"),
      }),
      reception: generateLink({
        propertyTicker: props.propertyTicker,
        mode: "reception",
        email: props.email,
        engineURL: props.engineURL + (isChain ? "/chain" : "/hotel"),
      }),
    };
  }, [props.propertyTicker, props.email, props.engineURL, isChain]);
  if (!isDreamplaceEngine(props.propertyTicker)) {
    return null;
  }
  if (!props.isCallCenterUser) {
    return (
      <a href={linkMap.reception} target="_blank" rel="noopener noreferrer">
        <TableActionButton>
          <FormattedMessage {...messages.insertReservation} />
        </TableActionButton>
      </a>
    );
  }

  return (
    <>
      <a href={linkMap.inbound} target="_blank" rel="noopener noreferrer">
        <TableActionButton>
          <FormattedMessage {...messages.insertReservationInbound} />
        </TableActionButton>
      </a>
      <a href={linkMap.outbound} target="_blank" rel="noopener noreferrer">
        <TableActionButton>
          <FormattedMessage {...messages.insertReservationOutbound} />
        </TableActionButton>
      </a>
    </>
  );
}

export function ImpersonateLoyaltyCustomer(
  props: ImpersonateLoyaltyCustomerProps
): JSX.Element | null {
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const isChain = Boolean(chainData?.isChain);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [session, setSession] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const handleHideModal = useCallback(() => {
    setLoading(false);
    setModalOpen(false);
  }, [setLoading]);
  const handleLinkSelection = useCallback(async () => {
    setLoading(true);
    setModalOpen(true);
    const session: string | undefined = await props.sessionBuilder(props.customerId, false);
    if (!session) {
      alert("Error occurred");
    }
    setLoading(false);
    setSession(session);
  }, [props]);

  const linkMap: Record<MODE, string> = useMemo(() => {
    return {
      inbound: generateLink({
        propertyTicker: props.propertyTicker,
        mode: "inbound",
        email: props.email,
        session,
        engineURL: props.engineURL + (isChain ? "/chain" : "/hotel"),
      }),
      outbound: generateLink({
        propertyTicker: props.propertyTicker,
        mode: "outbound",
        email: props.email,
        session,
        engineURL: props.engineURL + (isChain ? "/chain" : "/hotel"),
      }),
      reception: generateLink({
        propertyTicker: props.propertyTicker,
        mode: "reception",
        email: props.email,
        session,
        engineURL: props.engineURL + (isChain ? "/chain" : "/hotel"),
      }),
    };
  }, [props.propertyTicker, props.email, props.engineURL, session, isChain]);
  if (!isDreamplaceEngine(props.propertyTicker)) {
    return null;
  }

  return (
    <>
      <StyledReactTooltip id="login" effect="solid">
        <FormattedMessage {...messages.login} />
      </StyledReactTooltip>
      <Modal show={modalOpen} onHide={handleHideModal} size="xl">
        <Modal.Header closeButton>
          {props.isCallCenterUser ? (
            <FormattedMessage {...messages.insertReservationTitle} />
          ) : (
            <FormattedMessage {...messages.insertReservationCallCenterTitle} />
          )}
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <>
              <FormattedMessage {...defaultListMessages.loading} />
              <StyledLoader icon={faSyncAlt} spin />
            </>
          ) : (
            <div>
              {props.isCallCenterUser ? (
                <div style={{ display: "flex" }}>
                  <a href={linkMap.inbound} target="_blank" rel="noopener noreferrer">
                    <TableActionButton>
                      <FormattedMessage {...messages.insertReservationInbound} />
                    </TableActionButton>
                  </a>
                  <a href={linkMap.outbound} target="_blank" rel="noopener noreferrer">
                    <TableActionButton>
                      <FormattedMessage {...messages.insertReservationOutbound} />
                    </TableActionButton>
                  </a>
                </div>
              ) : (
                <div>
                  <a href={linkMap.reception} target="_blank" rel="noopener noreferrer">
                    <TableActionButton>
                      <FormattedMessage {...messages.insertReservation} />
                    </TableActionButton>
                  </a>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
      <Button
        disabled={false}
        data-tip="React-tooltip"
        data-for="login"
        size="sm"
        className="mr-2"
        variant="light"
        onClick={handleLinkSelection}>
        <FontAwesomeIcon icon={faSignInAlt} />
      </Button>
    </>
  );
}
